// T-29 — non-modal Explore Firefly catalog inside Ask (Activity dock).

import {
  fireflyTourDisplayTitle,
  type AccessibleFireflyTour,
} from "@/lib/firefly/listAccessibleFireflyTours";
import { FIREFLY_TARGET_ATTR } from "@/lib/firefly/fireflyTargetConvention";
import type { FireflyTourArtifact } from "@/lib/firefly/tourTypes";

export type ActivityDockFireflyCatalogProps = {
  entries: readonly AccessibleFireflyTour[];
  onStartTour: (artifact: FireflyTourArtifact) => void;
  onClose: () => void;
};

export function ActivityDockFireflyCatalog({
  entries,
  onStartTour,
  onClose,
}: ActivityDockFireflyCatalogProps) {
  return (
    <div
      {...{ [FIREFLY_TARGET_ATTR]: "firefly-catalog" }}
      className="space-y-2 rounded-lg border border-emerald-200/80 bg-white/90 p-2 dark:border-emerald-800/50 dark:bg-zinc-900/80"
      role="region"
      aria-label="Firefly tour catalog"
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <h4 className="text-[11px] font-semibold uppercase tracking-wide text-emerald-800 dark:text-emerald-200">
            What Firefly can do
          </h4>
          <p className="text-[11px] text-gray-600 dark:text-zinc-400">
            Tours you can start with your current roles. Pick one — nothing starts until you tap Start tour.
          </p>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="shrink-0 text-[11px] font-medium text-gray-500 hover:underline dark:text-zinc-400"
        >
          Close
        </button>
      </div>

      {entries.length === 0 ? (
        <p className="text-xs text-gray-600 dark:text-zinc-400" role="status">
          No tours are available for your roles in this workspace yet.
        </p>
      ) : (
        <ul className="max-h-48 space-y-1.5 overflow-y-auto">
          {entries.map(({ artifact, targetMode }) => (
            <li
              key={artifact.id}
              className="flex items-center justify-between gap-2 rounded-md border border-gray-100 bg-gray-50/80 px-2 py-1.5 dark:border-zinc-700 dark:bg-zinc-800/50"
            >
              <div className="min-w-0">
                <div className="truncate text-xs font-semibold text-gray-900 dark:text-zinc-100">
                  {fireflyTourDisplayTitle(artifact)}
                </div>
                <div className="truncate text-[10px] text-gray-500 dark:text-zinc-400">
                  Opens {targetMode}
                  {artifact.bucketRelevance?.length
                    ? ` · ${artifact.bucketRelevance.join(", ")}`
                    : ""}
                </div>
              </div>
              <button
                type="button"
                onClick={() => onStartTour(artifact)}
                className="shrink-0 rounded-md bg-emerald-600 px-2 py-1 text-[11px] font-semibold text-white hover:bg-emerald-700 dark:bg-emerald-700 dark:hover:bg-emerald-600"
              >
                Start tour
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
